import { Box } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import DetachTabButton from "./DetachTabButton";

export type TabId = "transcript" | "summary" | "metadata";

interface DetailTabsProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  detachedTabs?: Set<TabId>;
  onDetachTab?: (tabId: TabId) => void;
}

interface TabDef {
  id: TabId;
  label: string;
  badge?: string;
  flagGate?: string;
}

export default function DetailTabs({ activeTab, onTabChange, detachedTabs, onDetachTab }: Readonly<DetailTabsProps>) {
  const { t } = useTranslation(["common", "search"]);
  const { user } = useContext(AuthContext);

  const TABS: TabDef[] = [
    { id: "transcript", label: t("common:transcript") },
    { id: "summary", label: t("common:summary"), badge: t("common:ai"), flagGate: "Summarization" },
    { id: "metadata", label: t("common:metadata") },
  ];

  const visibleTabs = TABS.filter((tab) => {
    if (tab.flagGate) {
      return user?.hasFeatureFlag(tab.flagGate) ?? false;
    }
    return true;
  });

  return (
    <Box
      role="tablist"
      sx={{
        display: "flex",
        alignItems: "flex-end",
        borderBottom: "1px solid var(--border-subtle)",
        px: 2,
        flexShrink: 0,
      }}
    >
      {visibleTabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <Box
            key={tab.id}
            component="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(tab.id)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              px: 1.25,
              py: 1,
              fontSize: "0.8125rem",
              fontWeight: isActive ? 600 : 400,
              color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
              background: "none",
              border: "none",
              borderBottom: isActive
                ? "2px solid var(--accent)"
                : "2px solid transparent",
              cursor: "pointer",
              transition: "color 150ms ease, border-color 150ms ease",
              outline: "none",
              mb: "-1px",
              "&:hover": {
                color: "var(--text-primary)",
              },
            }}
          >
            {tab.label}
            {onDetachTab && (
              <DetachTabButton onClick={() => onDetachTab(tab.id)} />
            )}
            {tab.badge && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  color: "#fff",
                  background: "var(--archie-gradient)",
                  borderRadius: "4px",
                  px: "4px",
                  py: "1px",
                  letterSpacing: "0.04em",
                  lineHeight: 1.4,
                }}
              >
                {tab.badge}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
